import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Bar, Line } from 'react-chartjs-2';
import { Chart as GoogleChart } from 'react-google-charts';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import ViewComparisonTabs from './ViewComparisonTabs';
import { formatPatientCount, formatSiteCount, formatHCPCount } from '../../../utils/MathUtils';
import { FaCog } from 'react-icons/fa';

// Register Chart.js components including the datalabels plugin
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

// Define unmet needs mapping: Frontend Display Name -> Backend Column Name
// (Should match SystemDetailPage)
const UNMET_NEEDS_MAP = {
    "Excessive Steroid Use on Double Therapy": "Pct_ICS_LABA_High_Steroid_Usage",
    "Failure to Escalate after more than 3 ED/ER visits": "Pct_Failure_To_Escalate_With_3_Exacerbations",
    "Delay from Double to Advanced Therapy": "Pct_ICS_LABA_Escalation_Delay",
    "Biologic before triple": "Pct_Biologic_Before_Triple",
    "Open triple therapy": "Pct_ICS_LABA_With_LAMA"
};

// Create a reverse map: Backend Column Name -> Frontend Display Name
const BACKEND_TO_FRONTEND_UNMET_MAP = Object.fromEntries(
  Object.entries(UNMET_NEEDS_MAP).map(([key, value]) => [value, key])
);

const LocalView = ({ 
  selectedSystem, 
  priorityUnmetNeed, 
  systemProfileData, 
  keyUnmetNeedsData, 
  siteTreemapData,
  hospitalDistributionData,
  physicianDistributionData,
  handlePriorityNeedChange,
  unmetNeedsDisplayNames
}) => {
  // Remove local state for data fetched in SystemDetailPage
  // const [distributionData, setDistributionData] = useState([]);
  // const [selectedSystemPercentage, setSelectedSystemPercentage] = useState(0);
  // const [selectedSystemRange, setSelectedSystemRange] = useState('');
  // const [sitesData, setSitesData] = useState([]);
  // const [plansData, setPlansData] = useState([]); // Note: API doesn't seem to provide plans data
  // const [physiciansData, setPhysiciansData] = useState({ physicians: [], distribution: [] });
  // const [topUnmetNeedSystems, setTopUnmetNeedSystems] = useState([]); // Note: API doesn't seem to provide this
  // const [topOverlapSystems, setTopOverlapSystems] = useState([]); // Note: API doesn't seem to provide this
  // const [systemProfile, setSystemProfile] = useState({ ... });
  
  // Keep state for UI interactions
  const [activeComparison, setActiveComparison] = useState('local'); // Default comparison level
  const [selectedSiteType, setSelectedSiteType] = useState('All');
  const [filteredSitesData, setFilteredSitesData] = useState([]); // Derived from siteTreemapData
  const [cciAdjusted, setCciAdjusted] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [scaleValues, setScaleValues] = useState({ min: 'N/A', mid: 'N/A', max: 'N/A' });
  const [hospitalTooltipData, setHospitalTooltipData] = useState({}); // New state for tooltip

  // Helper function to get CCI category
  const getCciCategory = (cci) => {
    const value = parseFloat(cci);
    if (isNaN(value)) return ''; // Or some default like 'N/A'
    if (value < 0) return ''; // Or handle as error/invalid
    if (value >= 0 && value <= 0.5) return 'Very Low';
    if (value > 0.5 && value <= 1.0) return 'Low';
    if (value > 1.0 && value <= 1.5) return 'Low/Medium';
    if (value > 1.5 && value <= 2.0) return 'Medium';
    if (value > 2.0 && value <= 2.5) return 'Medium/High';
    if (value > 2.5 && value <= 3.0) return 'High';
    if (value > 3.0) return 'Very High';
    return ''; // Default for any unhandled cases, though above should cover valid positive CCI
  };

  // Derive filteredSitesData from siteTreemapData when it changes or site type changes
  useEffect(() => {
    if (siteTreemapData && siteTreemapData.length > 1) { // API returns root node + site nodes
       const sitesFromApi = siteTreemapData.filter(node => node.parent === selectedSystem.id).map(node => ({
         id: node.id, // Hospital Name acts as ID
         name: node.id, 
         value: node.value, // Patient Count for the need
         percentage: node.color, // API returns 0-100 percentage in color field
         patientCount: node.value, // Using value as patientCount (approximation)
         colorValue: node.color / 100, // Convert to 0-1 for color scale
         type: node.id.toLowerCase().includes('hospital') ? 'Hospital' : 'Clinic' 
       }));

      if (selectedSiteType === 'All') {
        setFilteredSitesData(sitesFromApi);
      } else {
        setFilteredSitesData(sitesFromApi.filter(site => site.type === selectedSiteType));
      }
    } else {
        setFilteredSitesData([]);
    }
  }, [siteTreemapData, selectedSiteType, selectedSystem.id]);

  // Calculate min/mid/max for treemap scale description
  useEffect(() => {
    if (filteredSitesData && filteredSitesData.length > 0) {
      // The colorValue is already in 0-1 range (percentage / 100)
      const colorValues = filteredSitesData.map(site => site.colorValue);
      const minActualColorVal = Math.min(...colorValues);
      const maxActualColorVal = Math.max(...colorValues);
      let midActualColorVal = (minActualColorVal + maxActualColorVal) / 2;

      // Handle case where all values are the same
      if (minActualColorVal === maxActualColorVal) {
        midActualColorVal = minActualColorVal; // Or maxActualColorVal, doesn't matter
      }

      setScaleValues({
        min: (minActualColorVal * 100).toFixed(1) + '%',
        mid: (midActualColorVal * 100).toFixed(1) + '%',
        max: (maxActualColorVal * 100).toFixed(1) + '%'
      });
    } else {
      setScaleValues({ min: 'N/A', mid: 'N/A', max: 'N/A' });
    }
  }, [filteredSitesData]);

  // Prepare detailed data for hospital distribution chart tooltip
  useEffect(() => {
    if (filteredSitesData && filteredSitesData.length > 0 && hospitalDistributionData?.bins) { 
      const newHospitalTooltipData = {};
      const allBins = hospitalDistributionData.bins;
      allBins.forEach(bin => {
        newHospitalTooltipData[bin] = [];
      });

      filteredSitesData.forEach(site => {
        const sitePercentage = parseFloat(site.percentage); 
        if (isNaN(sitePercentage)) return;

        for (let i = 0; i < allBins.length; i++) {
          const bin = allBins[i];
          const binParts = bin.replace(/%/g, '').split('-');
          const lowerBound = parseFloat(binParts[0]);
          let upperBound = Infinity; 
          
          let inBin = false;
          if (bin.includes('+')) { 
            if (sitePercentage >= lowerBound) inBin = true;
          } else if (binParts.length > 1) { 
            upperBound = parseFloat(binParts[1]);
            if (sitePercentage >= lowerBound && sitePercentage < upperBound) {
              inBin = true;
            } else if (i === allBins.length - 1 && sitePercentage >= lowerBound && sitePercentage <= upperBound) {
              inBin = true;
            }
          }

          if (inBin) {
            newHospitalTooltipData[bin].push({
              name: `${site.name} (${site.type || 'N/A'})`,
              patientCountForNeed: site.value, 
              unmetNeedPercentage: site.percentage 
            });
            break; 
          }
        }
      });

      for (const bin in newHospitalTooltipData) {
        newHospitalTooltipData[bin].sort((a, b) => b.patientCountForNeed - a.patientCountForNeed);
        newHospitalTooltipData[bin] = newHospitalTooltipData[bin].slice(0, 10);
      }
      setHospitalTooltipData(newHospitalTooltipData);
    } else {
      setHospitalTooltipData({});
    }
  }, [filteredSitesData, hospitalDistributionData]);

  // Create distribution data for filtered physicians (This is no longer possible with API data)
  // const getFilteredPhysicianDistribution = () => { ... }; // Remove this

  const handleComparisonChange = (level) => {
    setActiveComparison(level);
  };

  // Handle CCI adjustment toggle
  const handleCciAdjustmentToggle = () => {
    setCciAdjusted(!cciAdjusted);
    // Note: Add logic here if CCI adjustment needs to re-fetch or recalculate data.
    // The current API doesn't seem to support a CCI adjusted parameter.
    console.warn("CCI Adjustment toggle clicked, but API doesn't support adjustment yet.");
  };

  // Toggle settings modal
  const toggleSettingsModal = () => {
    setShowSettingsModal(!showSettingsModal);
  };

  // Check if essential data is loaded
  if (!systemProfileData || !keyUnmetNeedsData || !hospitalDistributionData || !physicianDistributionData) {
    // Should be handled by SystemDetailPage loading state, but as a fallback:
    return <div className="loading-container">Preparing view data...</div>;
  }

  // Extract data for easier access
  const profile = systemProfileData;
  const { key_unmet_needs: unmetNeedsApiData, trends: exacerbationTrendsApiData } = keyUnmetNeedsData;
  const hospitalDist = hospitalDistributionData;
  const physicianDist = physicianDistributionData;

  // Calculate system percentage for the priority context
  let systemPercentage = 'N/A';
  if (unmetNeedsApiData && priorityUnmetNeed) {
    const currentNeedData = unmetNeedsApiData.find(apiItem => 
        (BACKEND_TO_FRONTEND_UNMET_MAP[apiItem.name] || apiItem.name) === priorityUnmetNeed
    );
    if (currentNeedData && typeof currentNeedData.system === 'number') {
        systemPercentage = currentNeedData.system.toFixed(1);
    }
  }

  // --- Chart Data Preparation --- 

  // Custom plugin to ensure no text is displayed on markers (remains the same)
  const noLabelsPlugin = {
    id: 'noLabels',
    beforeDraw: (chart) => {
      const averageDatasets = chart.data.datasets.filter(dataset => 
        dataset.label === 'System Average' || dataset.label.includes('Average')
      );
      
      // Set datalabels to false for these datasets if they exist
      averageDatasets.forEach(dataset => {
        if (dataset.datalabels) {
          dataset.datalabels.display = false;
        }
      });
    }
  };

  // Map UI tabs to API average keys.
  const comparisonAvgKey = activeComparison; // 'local', 'state', or 'national'
  const comparisonAvgApiSuffix = `${activeComparison}_avg`; // ('local_avg', 'state_avg', 'national_avg')
  const comparisonAvgLabel = activeComparison.charAt(0).toUpperCase() + activeComparison.slice(1);

  // Hospital Distribution Chart
  const hospitalChartData = {
    labels: hospitalDist.bins, // Use bins from API
    datasets: [
      {
        label: 'Number of Sites',
        data: hospitalDist.bins.map(bin => hospitalDist.counts[bin] || 0), // Use counts from API
        backgroundColor: hospitalDist.bins.map(item => { // Color logic remains similar
          const rangeStart = parseInt(item);
          const rangeEnd = item.includes('+') ? 100 : parseInt(item.split('-')[1]);
          const midpoint = (rangeStart + rangeEnd) / 2;
          // ... (rest of color logic)
          if (midpoint <= 5) return 'rgba(0, 100, 0, 0.7)';
          if (midpoint <= 15) return 'rgba(34, 139, 34, 0.6)';
          if (midpoint <= 25) return 'rgba(144, 238, 144, 0.5)';
          if (midpoint <= 30) return 'rgba(220, 220, 220, 0.5)';
          if (midpoint <= 35) return 'rgba(255, 160, 122, 0.5)';
          if (midpoint <= 45) return 'rgba(178, 34, 34, 0.6)';
          return 'rgba(139, 0, 0, 0.7)';
        }),
         borderColor: hospitalDist.bins.map(item => { // Color logic remains similar
          const rangeStart = parseInt(item);
          const rangeEnd = item.includes('+') ? 100 : parseInt(item.split('-')[1]);
          const midpoint = (rangeStart + rangeEnd) / 2;
           if (midpoint <= 5) return 'rgba(0, 100, 0, 1)';
           if (midpoint <= 15) return 'rgba(34, 139, 34, 0.9)';
           if (midpoint <= 25) return 'rgba(144, 238, 144, 0.8)';
           if (midpoint <= 30) return 'rgba(169, 169, 169, 0.8)';
           if (midpoint <= 35) return 'rgba(255, 160, 122, 0.8)';
           if (midpoint <= 45) return 'rgba(178, 34, 34, 0.9)';
           return 'rgba(139, 0, 0, 1)';
         }),
        borderWidth: 1,
      },
      {
        label: 'System Average',
        data: hospitalDist.bins.map(bin => {
          const rangeStart = parseInt(bin);
          const rangeEnd = bin.includes('+') ? 100 : parseInt(bin.split('-')[1]);
          // Use system average from API
          return (hospitalDist.averages.system >= rangeStart && 
                  hospitalDist.averages.system < rangeEnd) ? 
                  Math.max(...Object.values(hospitalDist.counts)) * 0.8 : null; 
        }),
        backgroundColor: hospitalDist.averages.system > hospitalDist.averages[comparisonAvgKey] // Use dynamic key
          ? 'rgba(255, 99, 132, 0.8)' 
          : 'rgba(75, 192, 192, 0.8)',
        borderColor: hospitalDist.averages.system > hospitalDist.averages[comparisonAvgKey] // Use dynamic key
          ? 'rgba(255, 99, 132, 1)'
          : 'rgba(75, 192, 192, 1)',   
        borderWidth: 2,
        type: 'line',
        pointStyle: 'triangle',
        pointRadius: 18,
        pointHoverRadius: 22,
        showLine: false,
        datalabels: { display: false },
        display: true 
      },
      {
        label: `${comparisonAvgLabel} Average`,
        data: hospitalDist.bins.map(bin => {
           const rangeStart = parseInt(bin);
           const rangeEnd = bin.includes('+') ? 100 : parseInt(bin.split('-')[1]);
           // Use comparison average from API (local, state, or national)
           return (hospitalDist.averages[comparisonAvgKey] >= rangeStart && // Use dynamic key
                   hospitalDist.averages[comparisonAvgKey] < rangeEnd) ? // Use dynamic key
                   Math.max(...Object.values(hospitalDist.counts)) * 0.7 : null;
        }),
        backgroundColor: 'rgba(255, 152, 0, 0.8)',
        borderColor: 'rgba(255, 152, 0, 1)',
        borderWidth: 2,
        type: 'line',
        pointStyle: 'circle',
        pointRadius: 15,
        pointHoverRadius: 18,
        showLine: false,
        datalabels: { display: false },
        display: true 
      }
    ],
  };

  const hospitalChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          generateLabels: function(chart) {
            const originalLabels = ChartJS.defaults.plugins.legend.labels.generateLabels(chart);
            const filteredLabels = originalLabels.filter(
              label => !label.text.includes('Number of')
            );
            filteredLabels.forEach(label => {
              const dataset = chart.data.datasets[label.datasetIndex];
              if (dataset.label === 'System Average') {
                label.pointStyle = 'triangle';
                label.rotation = 0;
              } else if (dataset.label.includes('Average')) {
                label.pointStyle = 'circle';
              }
            });
            return filteredLabels;
          }
        }
      },
      title: {
        display: true,
        text: `Local Distribution of Sites by % of Patients with ${priorityUnmetNeed}${cciAdjusted ? ' (CCI Adjusted)' : ''}`,
        font: { size: 16 }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const label = context.dataset.label || '';
            const value = context.parsed.y;
            if (label === 'Number of Sites') {
              return `${label}: ${value}`;
            } else if (label === 'System Average') {
              const comparisonVal = hospitalDist.averages[comparisonAvgKey];
              const comparison = hospitalDist.averages.system > comparisonVal ? 'higher than' : 'lower than';
              return `${label}: ${hospitalDist.averages.system.toFixed(1)}% (${comparison} ${comparisonAvgLabel} average)`;
            } else { 
              return `${label}: ${hospitalDist.averages[comparisonAvgKey].toFixed(1)}%`;
            }
          },
          afterBody: function(context) {
            if (context.length > 0 && hospitalDist?.bins) {
              const dataPointIndex = context[0].dataIndex;
              const binLabel = hospitalDist.bins[dataPointIndex];
              const sitesInBin = hospitalTooltipData[binLabel];

              if (sitesInBin && sitesInBin.length > 0) {
                const tooltipLines = ['']; 
                tooltipLines.push('Top Sites in this bucket:');
                sitesInBin.forEach(site => {
                  tooltipLines.push(`  ${site.name}: ${site.patientCountForNeed} Pts (${site.unmetNeedPercentage.toFixed(1)}%)`);
                });
                return tooltipLines;
              }
            }
            return [];
          }
        }
      },
      averageLabels: noLabelsPlugin 
    },
    scales: {  
       y: { beginAtZero: true, title: { display: true, text: 'Number of Sites' } },
       x: { title: { display: true, text: '% of Patients with Priority Unmet Need' } },
    },
  };

  // Prepare treemap data for sites - Use filteredSitesData derived earlier
  const siteTreeMapChartData = [
    ["Site", "Parent", "Patient Value", "Percentage"],
    [selectedSystem.name, null, 0, 0], // Root node
    ...filteredSitesData.map(site => [
      site.name, 
      selectedSystem.name, // Parent is the system name
      site.value, // Patient count for the need
      site.colorValue // Use the 0-1 percentage value for color scale
    ])
  ];

  // Note: Plans Treemap cannot be generated as API doesn't provide plans data
  // const plansTreeMapData = [ ... ]; // Remove or comment out

  // Physician Distribution Chart
  // Uses aggregated data from physicianDist prop
  const physicianChartData = {
    labels: physicianDist.bins, // Use bins from API
    datasets: [
      {
        label: 'Number of Physicians',
        data: physicianDist.bins.map(bin => physicianDist.counts[bin] || 0), // Use counts from API
        backgroundColor: physicianDist.bins.map(item => { // Color logic remains similar
           const rangeStart = parseInt(item);
           const rangeEnd = item.includes('+') ? 100 : parseInt(item.split('-')[1]);
           const midpoint = (rangeStart + rangeEnd) / 2;
           // ... (rest of color logic)
           if (midpoint <= 5) return 'rgba(0, 100, 0, 0.7)';
           if (midpoint <= 15) return 'rgba(34, 139, 34, 0.6)';
           if (midpoint <= 25) return 'rgba(144, 238, 144, 0.5)';
           if (midpoint <= 30) return 'rgba(220, 220, 220, 0.5)';
           if (midpoint <= 35) return 'rgba(255, 160, 122, 0.5)';
           if (midpoint <= 45) return 'rgba(178, 34, 34, 0.6)';
           return 'rgba(139, 0, 0, 0.7)';
        }),
         borderColor: physicianDist.bins.map(item => { // Color logic remains similar
           const rangeStart = parseInt(item);
           const rangeEnd = item.includes('+') ? 100 : parseInt(item.split('-')[1]);
           const midpoint = (rangeStart + rangeEnd) / 2;
            if (midpoint <= 5) return 'rgba(0, 100, 0, 1)';
            if (midpoint <= 15) return 'rgba(34, 139, 34, 0.9)';
            if (midpoint <= 25) return 'rgba(144, 238, 144, 0.8)';
            if (midpoint <= 30) return 'rgba(169, 169, 169, 0.8)';
            if (midpoint <= 35) return 'rgba(255, 160, 122, 0.8)';
            if (midpoint <= 45) return 'rgba(178, 34, 34, 0.9)';
            return 'rgba(139, 0, 0, 1)';
          }),
        borderWidth: 1,
      },
      {
        label: 'System Average',
        data: physicianDist.bins.map(bin => {
          const rangeStart = parseInt(bin);
          const rangeEnd = bin.includes('+') ? 100 : parseInt(bin.split('-')[1]);
          // Use system average from API
          return (physicianDist.averages.system >= rangeStart && 
                  physicianDist.averages.system < rangeEnd) ? 
                  Math.max(...Object.values(physicianDist.counts)) * 0.8 : null;
        }),
        // Use system color, not comparison color here
        backgroundColor: 'rgba(63, 81, 181, 0.8)', 
        borderColor: 'rgba(63, 81, 181, 1)',
        borderWidth: 1,
        type: 'line',
        pointStyle: 'triangle',
        pointRadius: 15,
        pointHoverRadius: 18,
        showLine: false,
        datalabels: { display: false },
        display: true 
      },
      {
        label: `${comparisonAvgLabel} Average`,
        data: physicianDist.bins.map(bin => {
          const rangeStart = parseInt(bin);
          const rangeEnd = bin.includes('+') ? 100 : parseInt(bin.split('-')[1]);
          // Use comparison average from API (local, state, or national)
          return (physicianDist.averages[comparisonAvgKey] >= rangeStart && // Use dynamic key
                  physicianDist.averages[comparisonAvgKey] < rangeEnd) ? // Use dynamic key
                  Math.max(...Object.values(physicianDist.counts)) * 0.7 : null;
        }),
        backgroundColor: 'rgba(255, 152, 0, 0.8)',
        borderColor: 'rgba(255, 152, 0, 1)',
        borderWidth: 1,
        type: 'line',
        pointStyle: 'circle',
        pointRadius: 12, // Adjusted size from original
        pointHoverRadius: 15, // Adjusted size
        showLine: false,
        datalabels: { display: false }, 
        display: true
      }
    ],
  };

  const physicianChartOptions = {
    responsive: true,
    plugins: {
      noLabels: {}, 
      legend: {
        position: 'top',
        labels: {
          generateLabels: function(chart) {
            const originalLabels = ChartJS.defaults.plugins.legend.labels.generateLabels(chart);
            const filteredLabels = originalLabels.filter(
              label => !label.text.includes('Number of')
            );
            filteredLabels.forEach(label => {
              const dataset = chart.data.datasets[label.datasetIndex];
              if (dataset.label === 'System Average') {
                label.pointStyle = 'triangle';
                label.rotation = 0;
              } else if (dataset.label.includes('Average')) {
                label.pointStyle = 'circle';
              }
            });
            return filteredLabels;
          }
        }
      },
      title: {
        display: true,
        text: `Distribution of Physicians by Patient Percentage with ${priorityUnmetNeed}${cciAdjusted ? ' (CCI Adjusted)' : ''}`,
        font: { size: 16 }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const label = context.dataset.label || '';
            const value = context.parsed.y;
            
            if (label === 'Number of Physicians') {
              return `${label}: ${value}`;
            } else if (label === 'System Average') {
              return `${label}: ${physicianDist.averages.system.toFixed(1)}%`; // Use direct value
            } else { // Comparison Average
              return `${label}: ${physicianDist.averages[comparisonAvgKey].toFixed(1)}%`; // Use dynamic key
            }
          }
        }
      },
      averageLabels: noLabelsPlugin
    },
    scales: { /* y and x scales remain the same */ 
       y: { beginAtZero: true, title: { display: true, text: 'Number of Physicians' } },
       x: { title: { display: true, text: '% of Patients with Priority Unmet Need' } },
    },
  };

  // Exacerbation Trends Chart
  const exacerbationTrendsChartData = {
    labels: exacerbationTrendsApiData.map(item => `${item.quarter} 2023`), // Q1, Q2, Q3, Q4 with year
    datasets: [
      {
        label: 'Your System',
        data: exacerbationTrendsApiData.map(item => parseFloat(item.system)), // Use direct value (0-100)
        borderColor: 'rgb(255, 99, 132)',
        backgroundColor: 'rgba(255, 99, 132, 0.5)',
        tension: 0.1,
        fill: false
      },
      {
        label: `${comparisonAvgLabel} Average`,
        // Use local, state, or national average based on comparisonAvgKey
        data: exacerbationTrendsApiData.map(item => parseFloat(item[comparisonAvgApiSuffix])), // Use dynamic suffix
        borderColor: 'rgb(255, 152, 0)',
        backgroundColor: 'rgba(255, 152, 0, 0.5)',
        tension: 0.1,
        fill: false,
        borderDash: [5, 5]
      }
    ]
  };

  const exacerbationTrendsOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    plugins: {
      legend: { position: 'top' },
      title: { display: false, text: 'Trends in Exacerbations by Quarter', font: { size: 16 } },
      tooltip: { enabled: true, mode: 'index', intersect: false },
      datalabels: {
        display: true,
        anchor: 'end',
        align: 'top',
        formatter: (value, context) => {
            return `${value.toFixed(1)}%`;
        },
        font: {
            weight: 'bold'
        },
        color: '#555'
      }
    },
    scales: {
      x: { grid: { display: true } },
      y: {
        grid: { display: true },
        beginAtZero: true,
        min: 0,
        ticks: {
            callback: function(value) { return value + '%'; }
        },
        title: { display: true, text: 'Exacerbation Rate (%)' }
      }
    },
    elements: { line: { tension: 0.1 }, point: { radius: 3 } },
    hover: { mode: null, animationDuration: 0 },
    responsiveAnimationDuration: 0,
    events: ['click']
  };

  // Unmet Needs Chart
  const unmetNeedsChartData = {
    // API returns: { name: backend_col_name, system: %, local_avg: %, national_avg: % }
    // Map backend_col_name back to display name using the reverse map
    labels: unmetNeedsApiData.map(item => BACKEND_TO_FRONTEND_UNMET_MAP[item.name] || item.name), 
    datasets: [
      {
        label: 'Your System',
        data: unmetNeedsApiData.map(item => item.system), // Use direct value (0-100)
        backgroundColor: unmetNeedsApiData.map(item => 
          // Compare system vs comparison average (local, state, or national)
          item.system > item[comparisonAvgApiSuffix] ? 'rgba(255, 99, 132, 0.8)' : 'rgba(75, 192, 192, 0.8)'
        ),
        borderColor: unmetNeedsApiData.map(item => 
          item.system > item[comparisonAvgApiSuffix] ? 'rgba(255, 99, 132, 1)' : 'rgba(75, 192, 192, 1)'
        ),
        borderWidth: 1,
        pointStyle: 'circle'
      }
    ]
  };

  const unmetNeedsChartOptions = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      title: {
        display: false,
        text: `Key Unmet Needs by % of Patients${cciAdjusted ? ' (CCI Adjusted)' : ''}`,
        font: { size: 16 }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const datasetLabel = context.dataset.label || '';
            const value = context.parsed.x || 0; // Already percentage (0-100)
            const index = context.dataIndex;
            const apiItem = unmetNeedsApiData[index];
            const comparisonAvg = apiItem[comparisonAvgApiSuffix].toFixed(1); // Use dynamic suffix
            return [
              `${datasetLabel}: ${value.toFixed(1)}% of patients`,
              `${comparisonAvgLabel} Average: ${comparisonAvg}%`,
              `${value > comparisonAvg ? 'Above' : 'Below'} ${comparisonAvgLabel} average`
            ];
          }
        }
      },
      datalabels: {
        display: true,
        anchor: 'end',
        align: 'right',
        offset: -5,
        formatter: (value, context) => {
            return `${value.toFixed(1)}%`;
        },
        color: (context) => {
            return '#333';
        },
        font: {
            weight: 'bold'
        }
      }
    },
    scales: { 
       x: { beginAtZero: true, max: 100, title: { display: true, text: '% of Patients' } },
       y: {
         ticks: {
           font: {
             size: 13
           },
           color: '#000000'
         }
       }
     }
  };

  // Helper function for comparison color - Use profile data
  const getComparisonColor = (value, comparisonValue, lowerIsBetter = true) => {
    // Ensure comparisonValue is a number
    const compVal = parseFloat(comparisonValue);
    if (isNaN(compVal)) return 'text-gray-500'; // Default color if comparison is invalid
    
    if (lowerIsBetter) {
      return parseFloat(value) > compVal ? 'text-red-500' : 'text-green-500';
    } else {
      return parseFloat(value) < compVal ? 'text-red-500' : 'text-green-500';
    }
  };

  // Dynamic title based on activeComparison
  let viewTitle = `Local Comparison for ${selectedSystem.name}`;
  if (activeComparison === 'state') {
    viewTitle = `State Comparison for ${selectedSystem.name}`;
  } else if (activeComparison === 'national') {
    viewTitle = `National Comparison for ${selectedSystem.name}`;
  } else { // Default to local or if activeComparison is 'local'
    viewTitle = `Local System View for ${selectedSystem.name}`; 
  }

  return (
    <div className="local-view-container">
      {/* Priority Context - MOVED FROM HERE
      <div className="priority-context">
        <p className="selected-percentage">
          Approximately <strong>{systemPercentage}%</strong> of patients at <strong>{selectedSystem.name}</strong>
          have <strong>{priorityUnmetNeed}</strong>
        </p>
      </div>
      */}
      
      {/* Health System Profile - Use systemProfileData */}
      <div className="system-profile-container">
         <div className="system-profile-header">
           <h3>Health System Profile: {selectedSystem.name}</h3>
           <div className="flex items-center">
             <ViewComparisonTabs 
               activeComparison={activeComparison} 
               onComparisonChange={handleComparisonChange} 
               // Pass available levels if needed, API supports local/national
               availableLevels={['local', 'state', 'national']} // Keep UI tabs, map 'state' to national data
             />
             <button 
               onClick={toggleSettingsModal} 
               className="settings-button p-2 ml-4 rounded-full hover:bg-gray-100 transition-colors"
               aria-label="Settings"
             >
               <FaCog className="text-xl text-gray-600" />
             </button>
           </div>
         </div>
         
         {/* Settings Modal */}
         {showSettingsModal && (
           <div className="settings-modal-overlay fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
             <div className="settings-modal bg-white rounded-lg shadow-xl p-6 w-full max-w-5xl mx-4 flex flex-col max-h-[85vh]">
               <div className="flex justify-between items-center mb-4 flex-shrink-0">
                 <h3 className="text-xl font-semibold">Settings</h3>
                 <button 
                   onClick={toggleSettingsModal}
                   className="text-gray-500 hover:text-gray-700"
                 >
                   <span className="text-2xl">&times;</span>
                 </button>
               </div>
               
               <div className="settings-content overflow-y-auto">
                 {/* Data Source and Definitions - NEW SECTION */}
                 <div className="data-definitions-section mb-6 p-4 border border-gray-300 rounded-lg bg-white">
                   <h4 className="text-xl font-semibold mb-3 text-gray-800 border-b pb-2">Data Source & Definitions</h4>
                   
                   <div className="data-source mb-4">
                     <h5 className="text-md font-semibold mb-1 text-gray-700">Data Source</h5>
                     <p className="text-sm text-gray-600">
                       Analysis conducted on 11.9M asthma patients found in IQVIA claims data with an asthma treatment or diagnosis from 2019-2023 and their full history.
                     </p>
                   </div>

                   <div className="unmet-needs-definitions">
                     <h5 className="text-md font-semibold mb-2 text-gray-700">Unmet Need Definitions</h5>
                     <div className="space-y-3 text-sm text-gray-600">
                       <div>
                         <p className="font-medium text-gray-700">Excessive Steroid Use on Double Therapy:</p>
                         <p>Patients on double therapy who receive more than 450mg of OCS in a year. OCS is converted to prednisone equivalent milligrams, calculated using prescribed dose, quantity, and days supply.</p>
                       </div>
                       <div>
                         <p className="font-medium text-gray-700">Failure to Escalate after more than 3 ED/ER visits:</p>
                         <p>Patients on double therapy who experience exacerbations but are not escalated to triple therapy or biologics. Exacerbations are defined as visits to the ED/ER with a primary asthma diagnosis code or specific asthma treatments (e.g., emergency airway insertion, airway inhalation treatment, or nebulizer with compression).</p>
                       </div>
                       <div>
                         <p className="font-medium text-gray-700">Delay from Double to Advanced Therapy:</p>
                         <p>Patients who are escalated from double to advanced therapy more than 60 days after their first exacerbation. Exacerbations are defined as visits to the ED/ER with a primary asthma diagnosis code or specific asthma treatments (e.g., emergency airway insertion, airway inhalation treatment, or nebulizer with compression).</p>
                       </div>
                       <div>
                         <p className="font-medium text-gray-700">Open triple therapy:</p>
                         <p>Patients on double therapy who are escalated to a separate LAMA therapy.</p>
                       </div>
                       <div>
                         <p className="font-medium text-gray-700">Biologic before triple:</p>
                         <p>Patients on double therapy who receive a biologic prior to receiving a triple therapy.</p>
                       </div>
                     </div>
                   </div>
                 </div>

                 {/* CCI Adjustment Setting */}
                 <div className="cci-adjustment-setting mb-4 p-4 bg-blue-50 rounded-lg">
                   <div className="flex flex-col gap-3">
                     <div className="cci-description">
                       <h4 className="text-lg font-semibold mb-2 text-blue-800">Compare Health Systems with Fair Adjustment</h4>
                       <p className="text-gray-700">
                         The unmet need percentages can be adjusted for patient severity based on the Charlson Comorbidity Index (CCI). 
                         This helps create a fair comparison when patient severity and complexity is related to patient care.
                       </p>
                     </div>
                     <button 
                       onClick={handleCciAdjustmentToggle}
                       className={`px-4 py-2 rounded-md font-medium transition-colors duration-200 ${
                         cciAdjusted 
                           ? 'bg-blue-600 text-white hover:bg-blue-700' 
                           : 'bg-white text-blue-600 border-2 border-blue-600 hover:bg-blue-50'
                       }`}
                     >
                       {cciAdjusted ? 'Disable CCI Adjustment' : 'Adjust comparison across health systems for CCI'}
                     </button>
                     {cciAdjusted && (
                       <div className="mt-2 text-sm text-blue-600 bg-blue-50 p-2 border-l-4 border-blue-600">
                         <p><strong>CCI adjustment is enabled:</strong> Unmet need percentages are now normalized based on patient complexity.</p>
                       </div>
                     )}
                   </div>
                 </div>

                 {/* CCI Category Explanation */}
                 <div className="cci-category-explanation p-4 border border-gray-200 rounded-lg">
                   <h4 className="text-lg font-semibold mb-2 text-gray-700">Charlson Index Categories</h4>
                   <ul className="list-disc pl-5 text-sm text-gray-600 space-y-1">
                     <li><strong>0-0.5:</strong> Very Low</li>
                     <li><strong>0.5-1.0:</strong> Low</li>
                     <li><strong>1.0-1.5:</strong> Low/Medium</li>
                     <li><strong>1.5-2.0:</strong> Medium</li>
                     <li><strong>2.0-2.5:</strong> Medium/High</li>
                     <li><strong>2.5-3.0:</strong> High</li>
                     <li><strong>&gt;3.0:</strong> Very High</li>
                   </ul>
                 </div>
               </div>
               
               <div className="flex justify-end mt-4 flex-shrink-0">
                 <button 
                   onClick={toggleSettingsModal}
                   className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                 >
                   Close
                 </button>
               </div>
             </div>
           </div>
         )}
         
         {/* Example: System Overview */} 
         <div className="profile-stats-container grid grid-cols-1 gap-4 mb-6">
            <div className="profile-summary p-4 bg-white rounded-lg shadow">
              <h4 className="text-lg font-semibold mb-4 border-b pb-2">System Overview</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {/* Total Patients */}
                <div className="stat-item">
                  <div className="stat-value text-xl font-bold">{formatPatientCount(profile.total_patients)}</div>
                  <div className="stat-label text-sm text-gray-600">Total Patients</div>
                </div>
                {/* Number of Sites */}
                <div className="stat-item">
                  <div className="stat-value text-xl font-bold">{formatSiteCount(profile.num_sites)}</div>
                  <div className="stat-label text-sm text-gray-600">Number of Sites</div>
                </div>
                 {/* Number of HCPs */}
                <div className="stat-item">
                  <div className="stat-value text-xl font-bold">{formatHCPCount(profile.num_hcps)}</div>
                  <div className="stat-label text-sm text-gray-600">Number of HCPs</div>
                </div>
                {/* Commercial % */}
                <div className="stat-item">
                  <div className="stat-value text-xl font-bold">{profile.pct_commercial.toFixed(1)}%</div>
                  <div className="stat-label text-sm text-gray-600">Commercial
                    <span className={`ml-1 ${getComparisonColor(profile.pct_commercial, profile[comparisonAvgApiSuffix].pct_commercial, false)}`}>
                      ({profile[comparisonAvgApiSuffix].pct_commercial.toFixed(1)}% in {comparisonAvgLabel} average)
                    </span>
                  </div>
                </div>
                {/* Medicare % */}
                <div className="stat-item">
                  <div className="stat-value text-xl font-bold">{profile.pct_medicare.toFixed(1)}%</div>
                  <div className="stat-label text-sm text-gray-600">Medicare
                     <span className={`ml-1 ${getComparisonColor(profile.pct_medicare, profile[comparisonAvgApiSuffix].pct_medicare, true)}`}>
                       ({profile[comparisonAvgApiSuffix].pct_medicare.toFixed(1)}% in {comparisonAvgLabel} average)
                    </span>
                  </div>
                </div>
                {/* Medicaid % */}
                <div className="stat-item">
                  <div className="stat-value text-xl font-bold">{profile.pct_medicaid.toFixed(1)}%</div>
                  <div className="stat-label text-sm text-gray-600">Medicaid
                    <span className={`ml-1 ${getComparisonColor(profile.pct_medicaid, profile[comparisonAvgApiSuffix].pct_medicaid, true)}`}>
                       ({profile[comparisonAvgApiSuffix].pct_medicaid.toFixed(1)}% in {comparisonAvgLabel} average)
                    </span>
                  </div>
                </div>
                {/* Avg Age */}
                <div className="stat-item">
                  <div className="stat-value text-xl font-bold">{profile.avg_age.toFixed(1)}</div>
                  <div className="stat-label text-sm text-gray-600">Avg. Age
                    <span className={`ml-1 ${getComparisonColor(profile.avg_age, profile[comparisonAvgApiSuffix].avg_age, true)}`}>
                      ({profile[comparisonAvgApiSuffix].avg_age.toFixed(1)} in {comparisonAvgLabel} average)
                    </span>
                  </div>
                </div>
                {/* Avg CCI */}
                 <div className="stat-item">
                  <div className="stat-value text-xl font-bold">{profile.avg_cci.toFixed(1)} <span className="text-base font-normal text-gray-600">({getCciCategory(profile.avg_cci)})</span></div>
                  <div className="stat-label text-sm text-gray-600">Charlson Index
                     <span className={`ml-1 ${getComparisonColor(profile.avg_cci, profile[comparisonAvgApiSuffix].avg_cci, true)}`}>
                      ({profile[comparisonAvgApiSuffix].avg_cci.toFixed(1)} in {comparisonAvgLabel} average)
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Second Row - Charts (Unmet Needs & Exacerbations) */}
          <div className="profile-stats-container grid grid-cols-1 md:grid-cols-1 gap-4 mb-6">
            {/* Key Unmet Needs Chart */}
            <div className="profile-chart unmet-needs-chart">
              <h4 className="text-lg font-semibold mb-2">Key Unmet Needs</h4>
              <div className="chart-description">
                <p>This chart shows the percentage of patients with each unmet need. Comparing to 
                   <strong> {comparisonAvgLabel} average</strong>.
                   <span className="color-legend">
                     <span className="color-indicator red"></span> Above {comparisonAvgLabel} average
                     <span className="color-indicator green"></span> Below {comparisonAvgLabel} average
                   </span>
                </p>
              </div>
              <div style={{ height: '224px', position: 'relative' }}>
                <Bar data={unmetNeedsChartData} options={unmetNeedsChartOptions} />
              </div>
            </div>
            
            {/* Exacerbation Trends Chart */}
            <div className="profile-chart exacerbation-trends-chart">
              <h4 className="text-lg font-semibold mb-2">Trends in Exacerbations by Quarter</h4>
              <div className="chart-description">
                <p>This chart shows the trends in asthma exacerbation rates over the last four quarters. 
                The system's rates are compared against {comparisonAvgLabel} averages.</p>
              </div>
              <div style={{ height: '210px', position: 'relative' }}>
                <Line 
                  data={exacerbationTrendsChartData} // Use the prepared data
                  options={exacerbationTrendsOptions}
                />
              </div>
            </div>
          </div>
       </div> 

      {/* NEW: Priority Unmet Need Selector and Context MOVED HERE */}
      <div className="moved-priority-section mt-6 mb-6">
        {/* Priority Unmet Need Selector - PASTED HERE */}
        <div className="priority-selector-section">
          <h2 className="text-xl font-semibold mb-3">Priority Unmet Need</h2> {/* Styled h2 a bit */} 
          <div className="priority-selector">
            <select 
              value={priorityUnmetNeed} 
              onChange={handlePriorityNeedChange}
              className="priority-select block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            >
              {unmetNeedsDisplayNames.map(need => (
                <option key={need} value={need}>{need}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Priority Context - MOVED AND CORRECTED HERE */}
        <div className="priority-context mt-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded-md">
          <p className="selected-percentage">
            Approximately <strong>{systemPercentage}%</strong> of patients at <strong>{selectedSystem.name}</strong> have <strong>{priorityUnmetNeed}</strong>
          </p>
        </div>
      </div>
       
       {/* View Title - Dynamically updated */}
       <div className="current-view-heading">
         <h2>{viewTitle}</h2>
       </div>
       
       {/* Sites Treemap - Use siteTreeMapChartData */}
       <div className="sites-treemap-container">
          <div className="sites-header">
            <h3>Sites by Patient Volume with {priorityUnmetNeed}{cciAdjusted ? ' (CCI Adjusted)' : ''}</h3>
            <div className="site-type-filter">
              <label htmlFor="site-type">Filter by Site Type: </label>
              <select 
                id="site-type" 
                value={selectedSiteType} 
                onChange={(e) => setSelectedSiteType(e.target.value)}
                className="site-type-select"
                disabled={!siteTreemapData || siteTreemapData.length <= 1} // Disable if no site data
              >
                <option value="All">All Sites</option>
                {/* Types are hardcoded for now as API doesn't provide them */} 
                <option value="Hospital">Hospitals</option>
                <option value="Clinic">Clinics</option>
              </select>
            </div>
          </div>
          <div className="chart-description"> 
             <p>This treemap shows the {selectedSiteType === 'All' ? 'sites' : selectedSiteType.toLowerCase() + 's'} within {selectedSystem.name} sized by the number of patients with {priorityUnmetNeed}. 
              Larger boxes represent sites with more patients affected by this unmet need. 
              Colors indicate the percentage of patients with {priorityUnmetNeed}: Green (lowest, <strong>{scaleValues.min}</strong>), White (mid-range, <strong>{scaleValues.mid}</strong>), Red (highest, <strong>{scaleValues.max}</strong>).
              {cciAdjusted && <span className="ml-1 text-blue-600 font-medium"> (CCI Adjusted - Not yet implemented)</span>}
            </p>
          </div>
          <div className="treemap-container">
            {filteredSitesData.length > 0 ? (
              <GoogleChart
                chartType="TreeMap"
                width="100%"
                height="400px"
                data={siteTreeMapChartData}
                options={{
                  minColor: '#008000', // Green
                  midColor: '#FFFFFF', // White
                  maxColor: '#FF0000', // Red
                  headerHeight: 25,
                  fontColor: 'black',
                  showScale: true,
                  useWeightedAverageForAggregation: true,
                  generateTooltip: (row, size, value) => {
                    if (row === 0) return `<div style="padding:5px;"><b>${selectedSystem.name}</b></div>`; // Root tooltip
                    // Find the corresponding site data from filteredSitesData
                    const site = filteredSitesData[row - 1]; 
                    if (!site) return '';
                    return `<div style="padding:10px; background-color:#f5f5f5; border-radius:6px">
                      <strong>${site.name}</strong><br>
                      Type: ${site.type}<br> 
                      Patients with ${priorityUnmetNeed}: ${site.percentage.toFixed(1)}%<br>
                      Patient Volume (for need): ${site.value.toLocaleString()} 
                    </div>`;
                  }
                }}
              />
            ) : (
              <div className="no-data-container">No site data available for the selected filter.</div>
            )}
          </div>
       </div>
       
       {/* Hospital Distribution Chart - Use hospitalChartData */}
       <div className="chart-container">
          <div className="chart-description">
             <p>This chart shows the distribution of sites by the percentage of patients with {priorityUnmetNeed}. 
             The <strong>System Average</strong> is shown as a {hospitalDist.averages.system > hospitalDist.averages[comparisonAvgKey] ? 'red' : 'green'} triangle 
             (<strong>{hospitalDist.averages.system.toFixed(1)}%</strong>, <strong>{hospitalDist.averages.system > hospitalDist.averages[comparisonAvgKey] ? 'higher/worse' : 'lower/better'} than the {comparisonAvgLabel} average</strong>). 
             The <strong>{comparisonAvgLabel} Average</strong> is shown as an orange circle (<strong>{hospitalDist.averages[comparisonAvgKey].toFixed(1)}%</strong>).
             {cciAdjusted && <span className="ml-1 text-blue-600 font-medium"> (CCI Adjusted - Not yet implemented)</span>}
            </p>
          </div>
          <Bar data={hospitalChartData} options={hospitalChartOptions} />
       </div>
       
       {/* Physicians Container - Use physicianChartData */}
       <div className="physicians-container">
          <div className="physicians-header">
            <h3>Physicians by Patient Percentage with {priorityUnmetNeed}{cciAdjusted ? ' (CCI Adjusted)' : ''}</h3>
            {/* Update site count based on profile data, not filteredPhysicians */}
          </div>
          <div className="chart-description">
             <p>This chart shows the distribution of physicians within {selectedSystem.name} by the percentage of their 
              patients with {priorityUnmetNeed}. Each bar represents the number of physicians whose patients fall 
              within each percentage range. The <strong>System Average</strong> is shown as a blue triangle 
              (<strong>{physicianDist.averages.system.toFixed(1)}%</strong>, <strong>{physicianDist.averages.system > physicianDist.averages[comparisonAvgKey] ? 'higher/worse' : 'lower/better'} than the {comparisonAvgLabel} average</strong>). 
              The <strong>{comparisonAvgLabel} Average</strong> is shown as an orange circle (<strong>{physicianDist.averages[comparisonAvgKey].toFixed(1)}%</strong>).
              {cciAdjusted && <span className="ml-1 text-blue-600 font-medium"> (CCI Adjusted - Not yet implemented)</span>}
            </p>
          </div>
          <div className="chart-container">
            <Bar data={physicianChartData} options={physicianChartOptions} />
          </div>
       </div>
     </div>
   );
};

LocalView.propTypes = {
  selectedSystem: PropTypes.shape({
    // id is the system name from API
    id: PropTypes.string.isRequired, 
    name: PropTypes.string.isRequired, 
    // patientCount is available from the initial list fetch
    patientCount: PropTypes.number 
  }).isRequired,
  priorityUnmetNeed: PropTypes.string.isRequired,
  systemProfileData: PropTypes.object, 
  keyUnmetNeedsData: PropTypes.shape({
      key_unmet_needs: PropTypes.array,
      trends: PropTypes.array
  }),
  siteTreemapData: PropTypes.array, // Array of nodes
  hospitalDistributionData: PropTypes.shape({
      bins: PropTypes.array,
      counts: PropTypes.object,
      averages: PropTypes.object
  }),
  physicianDistributionData: PropTypes.shape({
      bins: PropTypes.array,
      counts: PropTypes.object,
      averages: PropTypes.object
  }),
  handlePriorityNeedChange: PropTypes.func.isRequired,
  unmetNeedsDisplayNames: PropTypes.array.isRequired,
};

export default LocalView; 